import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginationInfo } from 'src/app/shared/models';
import { MashoService } from 'src/app/services/masho-manager/masho.service';
import { MashoManagerModalService } from 'src/app/services/masho-manager-modal/masho-manager-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-masho-manager-listing',
  templateUrl: './masho-manager-listing.component.html',
  styleUrls: ['./masho-manager-listing.component.scss']
})
export class MashoManagerListingComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() mashoAdded = new EventEmitter<void>();
  @Output() mashoUpdated = new EventEmitter<void>();

  // Data properties
  mashos: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    masho_date_from: '',
    masho_date_to: '',
    valid_upto_from: '',
    valid_upto_to: '',
    search: ''
  };

  constructor(
    private _mashoService: MashoService,
    private _mashoManagerModalService: MashoManagerModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadMashos();
    this.setupModalSubscriptions();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  private setupModalSubscriptions(): void {
    // Subscribe to modal service changes if needed
    this.subscriptions.push(
      this._mashoManagerModalService.selectedMasho$.subscribe(masho => {
        if (masho) {
          console.log('Selected masho for editing:', masho);
        }
      })
    );
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg'; // Default placeholder image
  }

  private loadMashos(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    delete filters.search;

    this.subscriptions.push(
      this._mashoService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.mashos = response.data.data || [];
            this.updatePaginationInfo(response.data);
          } else {
            this.mashos = [];
            this.resetPagination();
          }
          this.cdr.detectChanges();
          this.initializeMenus();
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading mashos:', error);
          this.mashos = [];
          this.resetPagination();
          this.cdr.detectChanges();
        }
      })
    );
  }

  private updatePaginationInfo(data: any): void {
    this.paginationInfo = {
      currentPage: data.current_page || 1,
      perPage: data.per_page || 20,
      total: data.total || 0,
      lastPage: data.last_page || 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.paginationInfo.lastPage) {
      this.loadMashos(page, this.searchTerm, this.filters);
    }
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadMashos(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      masho_date_from: '',
      masho_date_to: '',
      valid_upto_from: '',
      valid_upto_to: '',
      search: ''
    };
    this.searchTerm = '';
    this.loadMashos(1, '', {});
  }

  openAddMashoModal(): void {
    this._mashoManagerModalService.clearSelectedMasho();
    const modalElement = document.getElementById('kt_modal_masho_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditMashoModal(masho: any): void {
    this._mashoManagerModalService.setSelectedMasho(masho);
    const modalElement = document.getElementById('kt_modal_masho_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteMasho(masho: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete this masho record?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(masho);
      }
    });
  }

  private performDelete(masho: any): void {
    const mashoId = masho._id || masho.id;
    this.subscriptions.push(
      this._mashoService.DeleteDataById(mashoId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Masho has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadMashos(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete masho',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting masho:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting masho',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onMashoAdded(): void {
    console.log('Masho added, refreshing list...');
    this.loadMashos(this.currentPage, this.searchTerm, this.filters);
    this.mashoAdded.emit();
  }

  onMashoUpdated(): void {
    console.log('Masho updated, refreshing list...');
    this.loadMashos(this.currentPage, this.searchTerm, this.filters);
    this.mashoUpdated.emit();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }
}
